import { SupermarketPreferences } from "@/clients/models/SupermarketPreferences";
import { QueryKeys } from "@/constants/query-keys";
import { useGetSupermarketOptions } from "@/hooks/requests/supermarket/use-get-supermarket-options";
import { useGetSupermarketPreferences } from "@/hooks/requests/supermarket/use-get-supermarket-preferences";
import { useUpdateSupermarketPreferences } from "@/hooks/requests/supermarket/use-update-supermarket-preferences";
import { useToast } from "@/hooks/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

interface UseSupermarketProps {
  onSuccess: () => void;
}

export const useSupermarket = ({ onSuccess }: UseSupermarketProps) => {
  const queryClient = useQueryClient();
  const { options, isLoading: isLoadingOptions } = useGetSupermarketOptions();
  const { preferences, isLoading: isLoadingPreferences } =
    useGetSupermarketPreferences();
  const { updateSupermarketPreferences, isUpdating } =
    useUpdateSupermarketPreferences();
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(),
  );
  const { showError } = useToast();
  const isLoading = isLoadingOptions || isLoadingPreferences;

  useEffect(() => {
    if (isLoadingOptions || isLoadingPreferences) return;
    if (options && preferences) {
      const validOptions = preferences.supermarketOptions
        ?.filter((preference) => {
          return options.some((option) => option === preference);
        })
        .filter(Boolean);
      setSelectedOptions(
        new Set(preferences.supermarketOptions ?? validOptions),
      );
    }
  }, [options, preferences, isLoading]);

  const handlePreferenceSelect = useCallback(
    (preference: string) => {
      return () => {
        setSelectedOptions((prev) => {
          const newSet = new Set(prev);
          if (prev.has(preference)) {
            newSet.delete(preference);
          } else {
            newSet.add(preference);
          }
          return newSet;
        });
      };
    },
    [setSelectedOptions],
  );

  const handleSubmit = () => {
    if (!selectedOptions || selectedOptions.size === 0) {
      showError("Please select at least one preference");
      return;
    }
    const dataBody: SupermarketPreferences = {
      supermarketOptions: Array.from(selectedOptions),
    };

    updateSupermarketPreferences(dataBody, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.SUPERMARKET_PREFERENCES],
        });
        onSuccess?.();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  return {
    options,
    preferences,
    selectedOptions,
    isLoading,
    handlePreferenceSelect,
    handleSubmit,
    isUpdating,
  };
};
